import React, { forwardRef, useImperativeHandle } from 'react';
import Typography from '@mui/material/Typography';
import {
  Alert,
  Card,
  CardHeader,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  Radio,
  Stack,
  TextField,
  InputAdornment,
} from '@mui/material';
import ListItemIcon from '@mui/material/ListItemIcon';
import { IColumn } from 'types/api/Tenant/EventDataSourceTypes';
import { matchSorter } from 'match-sorter';
import CancelIcon from '@mui/icons-material/Cancel';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import { HighlightedText } from 'components/HighlightedTextComponent';

const textInputStyles = {
  width: '100%',
  '& .MuiOutlinedInput-root': {
    padding: '2px 8px',
    borderRadius: 1.5,
    fontSize: 13,
  },
};

interface IListColumnProps {
  items: IColumn[];
  title: string;
  subheader: string;
  value?: IColumn | null;
  otherPairValue?: IColumn | null;
  onChange: (value: IColumn) => void;
  warningMessage?: string;
}

const chipStyle = {
  fontSize: 10,
  padding: '0 4px',
  height: 18,
  borderRadius: 0.75,
  fontWeight: 600,
};

export interface PairListColumnRef {
  resetSearch: () => void;
}

export const ListColumn = forwardRef<PairListColumnRef, IListColumnProps>((props, ref) => {
  const {
    items: listOptions,
    title,
    subheader,
    value: selectedValue,
    onChange = () => {},
    otherPairValue,
    warningMessage,
  } = props;

  const [searchKeyword, setSearchKeyword] = React.useState('');

  const items = React.useMemo(() => {
    if (listOptions.length > 0) {
      return matchSorter(listOptions, searchKeyword, { keys: ['columnName', 'dataType'] });
    }
    return [];
  }, [listOptions, searchKeyword]);

  useImperativeHandle(ref, () => ({
    resetSearch: () => setSearchKeyword(''),
  }));

  return (
    <Card
      elevation={0}
      sx={{
        width: '30%',
        border: '1px solid #E3EBF6',
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      <CardHeader
        sx={{ px: 2, py: 1.5, bgcolor: '#FAFBFD', borderBottom: '1px solid #E3EBF6' }}
        action={
          <TextField
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            name='search'
            sx={textInputStyles}
            size='small'
            placeholder='Search columns...'
            type='text'
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchOutlinedIcon sx={{ fontSize: 16, color: '#98A2AE' }} />
                </InputAdornment>
              ),
              endAdornment: searchKeyword ? (
                <CancelIcon
                  sx={{ fontSize: 14, color: '#98A2AE', cursor: 'pointer' }}
                  onClick={() => setSearchKeyword('')}
                />
              ) : null,
            }}
          />
        }
        title={
          <Stack>
            <Typography variant='input-label' fontSize={13} fontWeight={800}>
              {title}
            </Typography>
            <Typography variant='input-label-gray' fontSize={12}>
              {subheader}
            </Typography>
          </Stack>
        }
      />
      {warningMessage && (
        <Alert severity='warning' sx={{ borderRadius: 0, '& .MuiAlert-message': { fontSize: 12 } }}>
          {warningMessage}
        </Alert>
      )}
      <List
        sx={{
          height: 330,
          bgcolor: 'background.paper',
          overflow: 'auto',
        }}
        dense
        component='div'
        role='list'
      >
        {items.length === 0 && (
          <Stack alignItems='center' justifyContent='center' height='100%' px={2}>
            <Typography variant='input-label-gray' fontSize={13} textAlign='center'>
              {searchKeyword ? 'No columns match your search' : 'No columns available'}
            </Typography>
          </Stack>
        )}
        {items.map((column) => {
          const labelId = `transfer-list-all-item-${column.columnName}-label`;
          const isDisabled = !!(otherPairValue && otherPairValue?.dataType !== column.dataType);
          const isSelected = selectedValue?.id === column.id;

          return (
            <React.Fragment key={column.columnName}>
              <ListItem
                role='listitem'
                onClick={() => !isDisabled && onChange(column)}
                sx={{
                  opacity: isDisabled ? 0.35 : 1,
                  cursor: isDisabled ? 'not-allowed' : 'pointer',
                  py: 0.75,
                  px: 1,
                  bgcolor: isSelected ? '#EEF7F3' : 'transparent',
                  borderLeft: isSelected ? '3px solid #42BB93' : '3px solid transparent',
                  transition: 'all 0.15s ease',
                  '&:hover': {
                    bgcolor: isDisabled ? 'transparent' : isSelected ? '#EEF7F3' : '#F8F9FC',
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Radio
                    checked={isSelected}
                    disabled={isDisabled}
                    value={column.columnName}
                    sx={{
                      p: 0.5,
                      '&.Mui-checked': { color: '#42BB93' },
                    }}
                    name={column.id?.toString()}
                    inputProps={{ 'aria-labelledby': labelId }}
                  />
                </ListItemIcon>
                <ListItemText
                  id={labelId}
                  primary={
                    <Typography variant='body2' fontSize={13} fontWeight={isSelected ? 600 : 400}>
                      <HighlightedText text={column.columnName} highlight={searchKeyword} key={labelId} />
                    </Typography>
                  }
                  secondary={
                    <Chip
                      label={<HighlightedText text={column.dataTypeLabel || column.dataType} highlight={searchKeyword} key={labelId} />}
                      color='info'
                      variant='outlined'
                      sx={chipStyle}
                      size='small'
                    />
                  }
                />
              </ListItem>
              <Divider sx={{ borderColor: '#F0F3F7' }} />
            </React.Fragment>
          );
        })}
      </List>
    </Card>
  );
});
